// Deterministic models of a golden set and its grader, for Lesson 3.6 ("One Good Answer Is
// Not Evidence"). They replay recorded runs of the 30-note set through three matching rules,
// one graded run, a failure-mode table and a second run. They never call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export type Verdict = 'pass' | 'fail';
const WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
] as const;
/** A small count as a word; capitalized when it starts a sentence. */
export function countWord(count: number, capital = false): string {
  const word = WORDS[count] ?? String(count);
  return capital ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

// --- eval-match: which suggestion counts as right? -------------------------------------

export const MATCH_RULES = ['exact', 'normalized', 'keywords'] as const;
export type MatchRule = (typeof MATCH_RULES)[number];
export const matchRuleLabels: Record<MatchRule, string> = {
  exact: 'Exact text',
  normalized: 'Lowercase, no punctuation',
  keywords: 'Keywords',
};
export const toMatchRule = (value: string): MatchRule =>
  MATCH_RULES.find((rule) => rule === value) ?? 'exact';
export const EXPECTED_TASK_IDS = ['room', 'agenda', 'projector', 'microphone'] as const;
export type ExpectedTaskId = (typeof EXPECTED_TASK_IDS)[number];
export const toExpectedTaskId = (value: string): ExpectedTaskId =>
  EXPECTED_TASK_IDS.find((id) => id === value) ?? 'room';
export interface ExpectedTask {
  id: ExpectedTaskId;
  title: string;
  keywords: readonly string[];
}
export const ROOM_TASK: ExpectedTask = {
  id: 'room',
  title: 'Book the demo room',
  keywords: ['demo', 'room'],
};
/** Note 1's expected tasks in the golden set, each with the keywords the grader looks for. */
export const NOTE_ONE_EXPECTED: readonly ExpectedTask[] = [
  ROOM_TASK,
  { id: 'agenda', title: 'Send the agenda to Ravi', keywords: ['agenda'] },
  { id: 'projector', title: 'Check the projector', keywords: ['projector'] },
];
/** Note 2's expected task: the Apollo team tells the venue. */
export const NOTE_TWO_EXPECTED: ExpectedTask = {
  id: 'microphone',
  title: 'Tell the venue whether a microphone is needed',
  keywords: ['microphone'],
};
/** The recorded run 1 reply for note 1: three right answers in the model's own words. */
export const NOTE_ONE_CANDIDATES: readonly string[] = [
  'Book a room for the demo',
  'Send the agenda to Ravi by Friday',
  'Check if the projector in the small room is broken',
];
/** A title from note 2 written from the venue's side; a person rejects it. */
export const NOTE_TWO_CANDIDATE = 'Tell us whether you need a microphone';
/** Lowercase, strip punctuation, collapse spaces. */
export const normalize = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
/** Whether `candidate` matches `expected` under `rule`. */
export function matches(rule: MatchRule, expected: ExpectedTask, candidate: string): boolean {
  switch (rule) {
    case 'exact':
      return candidate === expected.title;
    case 'normalized':
      return normalize(candidate) === normalize(expected.title);
    case 'keywords': {
      const lower = candidate.toLowerCase();
      return expected.keywords.every((keyword) => lower.includes(keyword.toLowerCase()));
    }
  }
}
export interface MatchState {
  expected: ExpectedTaskId;
  rule: MatchRule;
  /** Whether note 2's title and its expected task are added to the set. */
  withNoteTwo: boolean;
}
export const initialMatch = (): MatchState => ({
  expected: 'room',
  rule: 'exact',
  withNoteTwo: false,
});
export const matchExpectedTasks = (state: MatchState): readonly ExpectedTask[] =>
  state.withNoteTwo ? [...NOTE_ONE_EXPECTED, NOTE_TWO_EXPECTED] : NOTE_ONE_EXPECTED;
export const matchCandidates = (state: MatchState): readonly string[] =>
  state.withNoteTwo ? [...NOTE_ONE_CANDIDATES, NOTE_TWO_CANDIDATE] : NOTE_ONE_CANDIDATES;
export const selectedTask = (state: MatchState): ExpectedTask =>
  matchExpectedTasks(state).find((task) => task.id === state.expected) ?? ROOM_TASK;
export type MatchAction =
  | { type: 'expected'; value: ExpectedTaskId }
  | { type: 'rule'; value: MatchRule }
  | { type: 'toggle-note-two' }
  | { type: 'reset' };
export function matchTransition(state: MatchState, action: MatchAction): MatchState {
  switch (action.type) {
    case 'reset':
      return initialMatch();
    case 'expected':
      if (action.value === 'microphone' && !state.withNoteTwo) return state;
      return { ...state, expected: action.value };
    case 'rule':
      return { ...state, rule: action.value };
    case 'toggle-note-two':
      return {
        ...state,
        withNoteTwo: !state.withNoteTwo,
        expected: state.withNoteTwo && state.expected === 'microphone' ? 'room' : state.expected,
      };
  }
}
export interface MatchRow {
  candidate: string;
  matched: boolean;
}
/** Each candidate against the selected expected task under the selected rule. */
export const matchRows = (state: MatchState): MatchRow[] =>
  matchCandidates(state).map((candidate) => ({
    candidate,
    matched: matches(state.rule, selectedTask(state), candidate),
  }));
export const matchText = (matched: boolean) => (matched ? 'matches' : 'does not match');
/** How many of note 1's three right answers some recorded suggestion matches under `rule`. */
export const rightAnswersMatched = (rule: MatchRule) =>
  NOTE_ONE_EXPECTED.filter((task) =>
    NOTE_ONE_CANDIDATES.some((candidate) => matches(rule, task, candidate)),
  ).length;
export const matchVerdict = (rule: MatchRule): string =>
  `${rightAnswersMatched(rule)} of ${NOTE_ONE_EXPECTED.length} right answers matched`;
export const NOTE_TWO_WARNING = "A person rejects this title: it is written from the venue's side.";
export function matchSummary(state: MatchState): Summary {
  const verdict = matchVerdict(state.rule);
  if (state.rule !== 'keywords')
    return {
      tone: 'warning',
      message: `${verdict}. The wording differs, so this rule marks a good reply wrong.`,
    };
  if (state.withNoteTwo && state.expected === 'microphone')
    return { tone: 'warning', message: `Keywords say yes. ${NOTE_TWO_WARNING}` };
  return {
    tone: 'success',
    message: `${verdict}. Every right answer carries its keyword, so no person is needed.`,
  };
}

// --- eval-run: what one run of the set tells us ------------------------------------------

export interface GradedNote {
  id: number;
  title: string;
  verdict: Verdict;
  reasons: readonly string[];
}
const pass = (id: number, title: string): GradedNote => ({
  id,
  title,
  verdict: 'pass',
  reasons: [],
});
const fail = (id: number, title: string, ...reasons: string[]): GradedNote => ({
  id,
  title,
  verdict: 'fail',
  reasons,
});
/** Run 1 of prompt version 1 over the 30-note golden set, recorded verdict by verdict. */
export const RUN_ONE: readonly GradedNote[] = [
  pass(1, 'Apollo standup'),
  pass(2, 'Venue email'),
  pass(3, 'Thank-you note'),
  pass(4, 'Planning call reminder'),
  fail(
    5,
    'Long planning note',
    'missing: Check the dates in the release note once the venue sends the final schedule',
  ),
  pass(6, 'Catering note'),
  pass(7, 'Support standup'),
  fail(8, 'Chat with Ravi', 'missing: Send the invoice address to the venue'),
  pass(9, 'Partner email'),
  pass(10, 'Retrospective'),
  pass(11, 'Monday agenda'),
  pass(12, 'Status update'),
  pass(13, 'Backup room'),
  pass(14, 'Questions answered'),
  pass(15, 'Overdue invoice'),
  pass(16, 'Two projects'),
  pass(17, 'Furniture order'),
  fail(
    18,
    'Vague wish',
    'extra: Think about accessibility',
    "forbidden 'accessibility': Think about accessibility",
  ),
  fail(19, 'Note to self', 'missing: Send the final headcount of 58 to Priya today'),
  pass(20, 'Two words'),
  pass(21, 'Demo day'),
  pass(22, 'Double booking'),
  pass(23, 'Chat with Jo'),
  pass(24, 'Ticket 311'),
  fail(25, 'Standup round', "missing: Review Ravi's release note"),
  fail(
    26,
    'Partner FAQ draft',
    'extra: Send the parking map',
    "forbidden 'parking': Send the parking map",
  ),
  pass(27, 'Needs list'),
  pass(28, 'Contract email'),
  pass(29, 'Support rota'),
  pass(30, 'All done'),
];
export const NOTE_COUNT = RUN_ONE.length;
export const RUN_VIEWS = ['reasons', 'score'] as const;
export type RunView = (typeof RUN_VIEWS)[number];
export const runViewLabels: Record<RunView, string> = {
  reasons: 'Pass or fail with reasons',
  score: 'A score out of 5',
};
export const toRunView = (value: string): RunView =>
  RUN_VIEWS.find((view) => view === value) ?? 'reasons';
export interface RunState {
  /** How many notes have been graded so far, in id order. */
  graded: number;
  view: RunView;
}
export const initialRun = (): RunState => ({ graded: 0, view: 'reasons' });
export type RunAction = { type: 'next' } | { type: 'view'; value: RunView } | { type: 'reset' };
export const canGradeNext = (state: RunState) => state.graded < NOTE_COUNT;
export function runTransition(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'reset':
      return initialRun();
    case 'view':
      return { ...state, view: action.value };
    case 'next':
      return canGradeNext(state) ? { ...state, graded: state.graded + 1 } : state;
  }
}
export const gradedNotes = (state: RunState): readonly GradedNote[] =>
  RUN_ONE.slice(0, state.graded);
export const passedCount = (notes: readonly GradedNote[]) =>
  notes.filter((note) => note.verdict === 'pass').length;
export const failedIds = (notes: readonly GradedNote[]) =>
  notes.filter((note) => note.verdict === 'fail').map((note) => note.id);
export const RUN_ONE_PASSED = passedCount(RUN_ONE);
export const RUN_ONE_FAILED_IDS = failedIds(RUN_ONE);
/** The score view: a pass is 5, a failure with one reason 3, with two reasons 2. */
export const scoreFor = (note: GradedNote) =>
  note.verdict === 'pass' ? 5 : note.reasons.length === 1 ? 3 : 2;
export const averageScore = (notes: readonly GradedNote[]) =>
  notes.length ? notes.reduce((sum, note) => sum + scoreFor(note), 0) / notes.length : 0;
export const formatScore = (score: number) => score.toFixed(1);
export const reasonsText = (note: GradedNote) => note.reasons.join('; ');
/** The running line under the table: a count of passes, or an average. */
export function runningLine(state: RunState): string {
  const notes = gradedNotes(state);
  if (!notes.length) return state.view === 'score' ? 'No score yet.' : 'Nothing graded yet.';
  if (state.view === 'score') return `${formatScore(averageScore(notes))} out of 5`;
  return `${passedCount(notes)} of ${notes.length} passed`;
}
export function runSummary(state: RunState): Summary {
  const notes = gradedNotes(state);
  if (!notes.length)
    return { tone: 'neutral', message: 'Nothing graded yet. Press Grade the next note.' };
  if (notes.length < NOTE_COUNT)
    return {
      tone: 'neutral',
      message: `${runningLine(state)} so far. One note is not the set, so keep going.`,
    };
  if (state.view === 'score')
    return {
      tone: 'warning',
      message: `${formatScore(averageScore(notes))} out of 5 says nothing about what to fix.`,
    };
  const passed = passedCount(notes);
  return {
    tone: 'success',
    message: `${passed} of ${NOTE_COUNT} passed, and ${countWord(NOTE_COUNT - passed)} reasons name what to fix.`,
  };
}

// --- eval-coding: what kind of failure is each one? --------------------------------------

export const FAILURE_MODES = [
  'unassigned',
  'implied',
  'conditional',
  'wish',
  'description',
  'instruction',
  'other',
] as const;
export type FailureMode = (typeof FAILURE_MODES)[number];
export const failureModeLabels: Record<FailureMode, string> = {
  unassigned: 'Unassigned',
  implied: 'Implied task skipped',
  conditional: 'Conditional task skipped',
  wish: 'Wish listed',
  description: 'Description listed',
  instruction: 'Instruction followed',
  other: 'Other',
};
export const toFailureMode = (value: string): FailureMode =>
  FAILURE_MODES.find((mode) => mode === value) ?? 'unassigned';
export const FAILURE_CARD_IDS = [5, 8, 18, 19, 25, 26] as const;
export type FailureCardId = (typeof FAILURE_CARD_IDS)[number];
export interface FailureCard {
  id: FailureCardId;
  title: string;
  reason: string;
}
/** The six run 1 failures, each with its first reason. */
export const FAILURE_CARDS: readonly FailureCard[] = FAILURE_CARD_IDS.map((id) => {
  const note = RUN_ONE.find((entry) => entry.id === id);
  return { id, title: note?.title ?? `Note ${id}`, reason: note?.reasons[0] ?? '' };
});
export type CodingState = Readonly<Record<FailureCardId, FailureMode>>;
const everyCard = (mode: FailureMode): CodingState => ({
  5: mode,
  8: mode,
  18: mode,
  19: mode,
  25: mode,
  26: mode,
});
export const initialCoding = (): CodingState => everyCard('unassigned');
export const allOtherCoding = (): CodingState => everyCard('other');
/** The grouping the lesson arrives at. */
export const INTENDED_CODING: CodingState = {
  5: 'conditional',
  8: 'implied',
  18: 'wish',
  19: 'instruction',
  25: 'implied',
  26: 'description',
};
export type CodingAction =
  { type: 'assign'; id: FailureCardId; mode: FailureMode } | { type: 'reset' };
export function codingTransition(state: CodingState, action: CodingAction): CodingState {
  switch (action.type) {
    case 'reset':
      return initialCoding();
    case 'assign':
      return { ...state, [action.id]: action.mode };
  }
}
export interface ModeCount {
  mode: FailureMode;
  label: string;
  count: number;
}
/**
 * The mode table: named modes by count descending then by name, zero counts left out,
 * Unassigned last while any card remains unassigned.
 */
export function modeTable(state: CodingState): ModeCount[] {
  const assigned = FAILURE_CARD_IDS.map((id) => state[id]);
  const counts = FAILURE_MODES.map((mode) => ({
    mode,
    label: failureModeLabels[mode],
    count: assigned.filter((entry) => entry === mode).length,
  })).filter((entry) => entry.count > 0);
  const named = counts
    .filter((entry) => entry.mode !== 'unassigned')
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return [...named, ...counts.filter((entry) => entry.mode === 'unassigned')];
}
export const modeLine = (entry: ModeCount) => `${entry.label}: ${entry.count}`;
export const namedCount = (state: CodingState) =>
  FAILURE_CARD_IDS.filter((id) => state[id] !== 'unassigned').length;
export function codingSummary(state: CodingState): Summary {
  const named = namedCount(state);
  const total = FAILURE_CARD_IDS.length;
  if (named === total) {
    const table = modeTable(state);
    const [first] = table;
    if (table.every((entry) => entry.mode === 'other'))
      return { tone: 'warning', message: `Other: ${total} says nothing. Name the kinds.` };
    if (first && table.every((entry) => entry.mode !== 'other')) {
      const modes = table.length === 1 ? 'One mode' : `${countWord(table.length, true)} modes`;
      return {
        tone: 'success',
        message: `${modes}, biggest first: ${first.label} ${first.count}. Fix that first.`,
      };
    }
  }
  return { tone: 'neutral', message: `${named} of ${total} failures named.` };
}

// --- eval-sampling: is one run the answer? -----------------------------------------------

export const SAMPLING_NOTE_IDS = [6, 9, 5, 26, 1, 12] as const;
export type SamplingNoteId = (typeof SAMPLING_NOTE_IDS)[number];
export const toSamplingNoteId = (value: string): SamplingNoteId =>
  SAMPLING_NOTE_IDS.find((id) => String(id) === value) ?? 6;
export interface RunRecord {
  suggestions: readonly string[];
  verdict: Verdict;
  reasons: readonly string[];
}
export interface SampledNote {
  id: SamplingNoteId;
  title: string;
  /** Run 1, then run 2. */
  runs: readonly [RunRecord, RunRecord];
}
const passed = (...suggestions: string[]): RunRecord => ({
  suggestions,
  verdict: 'pass',
  reasons: [],
});
const failed = (suggestions: readonly string[], ...reasons: string[]): RunRecord => ({
  suggestions,
  verdict: 'fail',
  reasons,
});
/** Two recorded runs at temperature 0 with prompt version 1, for six of the 30 notes. */
export const SAMPLED_NOTES: Readonly<Record<SamplingNoteId, SampledNote>> = {
  6: {
    id: 6,
    title: 'Catering note',
    runs: [
      passed('Remind Jo about the parking passes'),
      failed([], 'missing: Remind Jo about the parking passes'),
    ],
  },
  9: {
    id: 9,
    title: 'Partner email',
    runs: [
      passed('Add Anita and two others to the guest list', 'Let Anita know where to park'),
      failed(
        [],
        'missing: Add the three Northwind people to the guest list',
        'missing: Tell Anita where to park',
      ),
    ],
  },
  5: {
    id: 5,
    title: 'Long planning note',
    runs: [
      failed(
        [
          'Order ten more name badges for the partners before the 10th',
          'Send the parking map to Jo',
        ],
        'missing: Check the dates in the release note once the venue sends the final schedule',
      ),
      passed(
        'Check dates in release note draft once venue sends final schedule',
        'Order ten more name badges for partners before the 10th',
        'Send parking map to Jo',
      ),
    ],
  },
  26: {
    id: 26,
    title: 'Partner FAQ draft',
    runs: [
      failed(
        ['Send the parking map'],
        'extra: Send the parking map',
        "forbidden 'parking': Send the parking map",
      ),
      passed(),
    ],
  },
  1: {
    id: 1,
    title: 'Apollo standup',
    runs: [
      passed(
        'Book a room for the demo',
        'Send the agenda to Ravi by Friday',
        'Check if the projector in the small room is broken',
      ),
      passed(
        'Book a room for the demo',
        'Send agenda to Ravi by Friday',
        'Check if the projector in the small room is broken',
      ),
    ],
  },
  12: {
    id: 12,
    title: 'Status update',
    runs: [
      passed('Chase the venue about the schedule'),
      passed('Chase the venue about the schedule tomorrow'),
    ],
  },
};
export const SAMPLING_RUNS = ['run-1', 'run-2', 'both'] as const;
export type SamplingRun = (typeof SAMPLING_RUNS)[number];
export const samplingRunLabels: Record<SamplingRun, string> = {
  'run-1': 'Run 1',
  'run-2': 'Run 2',
  both: 'Both runs',
};
export const toSamplingRun = (value: string): SamplingRun =>
  SAMPLING_RUNS.find((run) => run === value) ?? 'run-1';
export interface SamplingState {
  note: SamplingNoteId;
  run: SamplingRun;
}
export const initialSampling = (): SamplingState => ({ note: 6, run: 'run-1' });
export type Agreement = 'identical' | 'wording' | 'flipped';
/** How the two runs compare on one note. */
export function classifyNote(note: SampledNote): Agreement {
  const [first, second] = note.runs;
  if (first.verdict !== second.verdict) return 'flipped';
  const same =
    first.suggestions.length === second.suggestions.length &&
    first.suggestions.every((title, index) => title === second.suggestions[index]);
  return same ? 'identical' : 'wording';
}
export function agreementLine(note: SampledNote): string {
  switch (classifyNote(note)) {
    case 'identical':
      return 'identical lists, same verdict';
    case 'wording':
      return 'same verdict, different wording';
    case 'flipped': {
      const [first, second] = note.runs;
      return `verdict flips: ${first.verdict} then ${second.verdict}`;
    }
  }
}
/** Totals over all 30 notes, recorded from the two runs. */
export const SAMPLING_TOTALS = {
  run1Passed: 24,
  run2Passed: 24,
  identical: 19,
  wording: 11,
  flipped: [5, 6, 9, 26],
} as const;
export const totalsLine = () =>
  `Run 1: ${SAMPLING_TOTALS.run1Passed} of ${NOTE_COUNT}. Run 2: ${SAMPLING_TOTALS.run2Passed} of ${NOTE_COUNT}. Identical lists: ${SAMPLING_TOTALS.identical} of ${NOTE_COUNT}. Flipped verdicts: ${SAMPLING_TOTALS.flipped.length} (notes ${SAMPLING_TOTALS.flipped.join(', ')}).`;
/** One run's panel: the numbered suggestions, then the verdict and its reasons. */
export function runText(record: RunRecord): string {
  const list = record.suggestions.length
    ? record.suggestions.map((title, index) => `${index + 1}. ${title}`).join('\n')
    : '(no suggestions)';
  return [list, '', `verdict: ${record.verdict}`, ...record.reasons].join('\n');
}
export interface ShownRun {
  label: string;
  record: RunRecord;
}
export function shownRuns(state: SamplingState): ShownRun[] {
  const [first, second] = SAMPLED_NOTES[state.note].runs;
  const one = { label: samplingRunLabels['run-1'], record: first };
  const two = { label: samplingRunLabels['run-2'], record: second };
  if (state.run === 'run-1') return [one];
  if (state.run === 'run-2') return [two];
  return [one, two];
}
export function samplingSummary(state: SamplingState): Summary {
  const note = SAMPLED_NOTES[state.note];
  if (state.run === 'both') {
    const agreement = classifyNote(note);
    if (agreement === 'flipped')
      return {
        tone: 'warning',
        message: `Judging note ${note.id} from one run would be wrong half the time, even at temperature 0.`,
      };
    return {
      tone: 'success',
      message:
        agreement === 'wording'
          ? 'Both runs give the same verdict with different wording.'
          : 'Both runs give the same verdict and the same list.',
    };
  }
  const [first, second] = note.runs;
  const record = state.run === 'run-1' ? first : second;
  const rule = note.id === 6 ? 'the data rule' : 'the prompt';
  return {
    tone: 'neutral',
    message: `One run says ${rule} ${record.verdict === 'pass' ? 'works' : 'fails'} for this note.`,
  };
}
